/**
 * Gráfico de Comparativo Mensal
 * Mostra comparação mensal do fluxo de pessoas com filtros de 3, 6 ou 12 meses
 * Suporta dados históricos importados (ex: Mais Fluxo) com barras empilhadas
 * Modo "Comparativo Anual": barras lado a lado comparando 2025 vs 2026
 */

import React, { useState, useMemo } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from 'recharts';
import { Calendar, TrendingUp, TrendingDown, Users, ArrowRight, BarChart3 } from 'lucide-react';

type MonthsFilter = 3 | 6 | 12;
type ViewMode = 'sequential' | 'yearly';

export interface MonthlyData {
  month: string;
  month_name: string;
  people_count: number;
  detections_count: number;
  growth_percentage?: number;
  source?: 'awflux' | 'historical' | 'mixed';
  people_awflux?: number;
  people_historical?: number;
}

interface MonthlyComparisonChartProps {
  data?: MonthlyData[];
  className?: string;
}

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export const MonthlyComparisonChart: React.FC<MonthlyComparisonChartProps> = ({
  data = [],
  className = '',
}) => {
  const [monthsFilter, setMonthsFilter] = useState<MonthsFilter>(12);
  const [viewMode, setViewMode] = useState<ViewMode>('sequential');

  // Verificar se tem dados históricos para habilitar comparativo anual
  const hasHistoricalData = useMemo(() => {
    return data.some(d => d.source === 'historical' || d.source === 'mixed');
  }, [data]);

  // Descobrir anos disponíveis nos dados
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    data.forEach(d => {
      const year = parseInt(d.month.split('-')[0]);
      if (!isNaN(year)) years.add(year);
    });
    return Array.from(years).sort();
  }, [data]);

  // ============================================================
  // MODO SEQUENCIAL (comportamento original)
  // ============================================================
  const filteredData = useMemo(() => {
    if (data.length === 0 || viewMode === 'yearly') return [];
    const sorted = [...data].sort((a, b) => a.month.localeCompare(b.month));
    const sliced = sorted.slice(-monthsFilter);
    return sliced.map((item, index) => {
      const prevMonth = index > 0 ? sliced[index - 1] : null;
      const growth = prevMonth && prevMonth.people_count > 0
        ? ((item.people_count - prevMonth.people_count) / prevMonth.people_count) * 100
        : 0;
      return { ...item, growth_percentage: Math.round(growth) };
    });
  }, [data, monthsFilter, viewMode]);

  // ============================================================
  // MODO COMPARATIVO ANUAL (Jan-Dez lado a lado)
  // ============================================================
  const yearlyComparisonData = useMemo(() => {
    if (viewMode !== 'yearly' || availableYears.length < 2) return [];

    // Pegar os dois últimos anos disponíveis
    const [yearA, yearB] = availableYears.slice(-2);

    // Agrupar dados por mês do ano
    const byYearMonth: Record<number, Record<number, number>> = {};
    data.forEach(d => {
      const [yStr, mStr] = d.month.split('-');
      const y = parseInt(yStr);
      const m = parseInt(mStr);
      if (isNaN(y) || isNaN(m)) return;
      if (y !== yearA && y !== yearB) return;
      if (!byYearMonth[m]) byYearMonth[m] = {};
      byYearMonth[m][y] = (byYearMonth[m][y] || 0) + d.people_count;
    });

    // Gerar array para os 12 meses
    const result = [];
    for (let m = 1; m <= 12; m++) {
      const valA = byYearMonth[m]?.[yearA] || 0;
      const valB = byYearMonth[m]?.[yearB] || 0;
      const diff = valA > 0 ? Math.round(((valB - valA) / valA) * 100) : 0;
      result.push({
        month_label: MONTH_LABELS[m - 1],
        [`year_${yearA}`]: valA,
        [`year_${yearB}`]: valB,
        diff_pct: diff,
        yearA,
        yearB,
      });
    }
    return result;
  }, [data, viewMode, availableYears]);

  // Estatísticas (modo sequencial)
  const statistics = useMemo(() => {
    const items = viewMode === 'sequential' ? filteredData : [];
    if (items.length === 0) {
      return { total: 0, average: 0, trend: 'neutral' as const, growth: 0, bestMonth: null as MonthlyData | null };
    }
    const total = items.reduce((sum, d) => sum + d.people_count, 0);
    const average = Math.round(total / items.length);
    const halfPoint = Math.floor(items.length / 2);
    const firstHalf = items.slice(0, halfPoint);
    const secondHalf = items.slice(halfPoint);
    const firstAvg = firstHalf.reduce((sum, d) => sum + d.people_count, 0) / (firstHalf.length || 1);
    const secondAvg = secondHalf.reduce((sum, d) => sum + d.people_count, 0) / (secondHalf.length || 1);
    const growth = firstAvg > 0 ? ((secondAvg - firstAvg) / firstAvg) * 100 : 0;
    const trend = growth > 10 ? 'up' as const : growth < -10 ? 'down' as const : 'neutral' as const;
    const bestMonth = items.reduce((max, d) => (d.people_count > max.people_count ? d : max), items[0]);
    return { total, average, trend, growth: Math.round(growth), bestMonth };
  }, [filteredData, viewMode]);

  // Estatísticas anuais
  const yearlyStats = useMemo(() => {
    if (viewMode !== 'yearly' || yearlyComparisonData.length === 0 || availableYears.length < 2) {
      return null;
    }
    const [yearA, yearB] = availableYears.slice(-2);
    const keyA = `year_${yearA}`;
    const keyB = `year_${yearB}`;
    const totalA = yearlyComparisonData.reduce((sum, d) => sum + ((d as any)[keyA] || 0), 0);
    const totalB = yearlyComparisonData.reduce((sum, d) => sum + ((d as any)[keyB] || 0), 0);
    const overallDiff = totalA > 0 ? Math.round(((totalB - totalA) / totalA) * 100) : 0;
    return { yearA, yearB, totalA, totalB, overallDiff };
  }, [yearlyComparisonData, viewMode, availableYears]);

  // Tooltip sequencial
  const SequentialTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">
          {d.month_name}
          {d.source === 'historical' && <span className="ml-2 text-xs font-normal text-orange-500">(Importado)</span>}
          {d.source === 'mixed' && <span className="ml-2 text-xs font-normal text-blue-500">(Combinado)</span>}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Total: <span className="font-medium text-purple-600 dark:text-purple-400">{d.people_count.toLocaleString('pt-BR')}</span>
        </p>
        {d.source === 'mixed' && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              AWFlux: <span className="font-medium text-purple-600">{(d.people_awflux || 0).toLocaleString('pt-BR')}</span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Importado: <span className="font-medium text-orange-500">{(d.people_historical || 0).toLocaleString('pt-BR')}</span>
            </p>
          </>
        )}
        {d.source !== 'historical' && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Detecções: <span className="font-medium">{d.detections_count.toLocaleString('pt-BR')}</span>
          </p>
        )}
        {d.growth_percentage !== undefined && d.growth_percentage !== 0 && (
          <p className={`text-sm font-medium mt-1 ${d.growth_percentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {d.growth_percentage > 0 ? '↑' : '↓'} {Math.abs(d.growth_percentage)}% vs mês anterior
          </p>
        )}
      </div>
    );
  };

  // Tooltip anual
  const YearlyTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const valA = d[`year_${d.yearA}`] || 0;
    const valB = d[`year_${d.yearB}`] || 0;
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{d.month_label}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {d.yearA}: <span className="font-medium text-orange-500">{valA.toLocaleString('pt-BR')}</span>
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {d.yearB}: <span className="font-medium text-purple-600">{valB.toLocaleString('pt-BR')}</span>
        </p>
        {d.diff_pct !== 0 && (
          <p className={`text-sm font-medium mt-1 ${d.diff_pct > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {d.diff_pct > 0 ? '↑' : '↓'} {Math.abs(d.diff_pct)}% de variação
          </p>
        )}
      </div>
    );
  };

  const hasSeqHistorical = useMemo(() => {
    return filteredData.some(d => d.source === 'historical' || d.source === 'mixed');
  }, [filteredData]);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Calendar className="w-6 h-6 text-purple-600" />
            {viewMode === 'yearly' ? 'Comparativo Anual' : 'Comparativo Mensal'}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {viewMode === 'yearly'
              ? `${availableYears.slice(-2).join(' vs ')} — mês a mês`
              : 'Evolução do fluxo por mês'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle de modo (só aparece se tem dados históricos) */}
          {hasHistoricalData && availableYears.length >= 2 && (
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg mr-2">
              <button
                onClick={() => setViewMode('sequential')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'sequential'
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                Sequencial
              </button>
              <button
                onClick={() => setViewMode('yearly')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                  viewMode === 'yearly'
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                <BarChart3 className="w-3 h-3" />
                Ano vs Ano
              </button>
            </div>
          )}

          {/* Filtros de período (só no modo sequencial) */}
          {viewMode === 'sequential' && (
            <div className="flex gap-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
              {([3, 6, 12] as MonthsFilter[]).map((months) => (
                <button
                  key={months}
                  onClick={() => setMonthsFilter(months)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    monthsFilter === months
                      ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {months}m
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* MODO ANUAL — Estatísticas */}
      {/* ============================================================ */}
      {viewMode === 'yearly' && yearlyStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-xl p-4 border border-orange-200 dark:border-orange-700">
            <div className="text-sm text-orange-600 dark:text-orange-400 font-medium mb-1">
              Total {yearlyStats.yearA}
            </div>
            <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {yearlyStats.totalA.toLocaleString('pt-BR')}
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
            <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">
              Total {yearlyStats.yearB}
            </div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {yearlyStats.totalB.toLocaleString('pt-BR')}
            </div>
          </div>
          <div className={`bg-gradient-to-br rounded-xl p-4 border ${
            yearlyStats.overallDiff > 0
              ? 'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700'
              : yearlyStats.overallDiff < 0
              ? 'from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200 dark:border-red-700'
              : 'from-gray-50 to-gray-100 dark:from-gray-700/20 dark:to-gray-600/20 border-gray-200 dark:border-gray-600'
          }`}>
            <div className={`text-sm font-medium mb-1 flex items-center gap-1 ${
              yearlyStats.overallDiff > 0 ? 'text-green-600 dark:text-green-400'
              : yearlyStats.overallDiff < 0 ? 'text-red-600 dark:text-red-400'
              : 'text-gray-600 dark:text-gray-400'
            }`}>
              {yearlyStats.overallDiff > 0 ? <TrendingUp className="w-4 h-4" /> : yearlyStats.overallDiff < 0 ? <TrendingDown className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              Variação Total
            </div>
            <div className={`text-2xl font-bold ${
              yearlyStats.overallDiff > 0 ? 'text-green-900 dark:text-green-100'
              : yearlyStats.overallDiff < 0 ? 'text-red-900 dark:text-red-100'
              : 'text-gray-900 dark:text-gray-100'
            }`}>
              {yearlyStats.overallDiff > 0 ? '+' : ''}{yearlyStats.overallDiff}%
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
            <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1 flex items-center gap-1">
              <Users className="w-4 h-4" />
              Diferença Absoluta
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {Math.abs(yearlyStats.totalB - yearlyStats.totalA).toLocaleString('pt-BR')}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODO SEQUENCIAL — Estatísticas */}
      {/* ============================================================ */}
      {viewMode === 'sequential' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
            <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1 flex items-center gap-1">
              <Users className="w-4 h-4" />
              Total ({monthsFilter} meses)
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {statistics.total.toLocaleString('pt-BR')}
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
            <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">Média Mensal</div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {statistics.average.toLocaleString('pt-BR')}
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border border-green-200 dark:border-green-700">
            <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1 flex items-center gap-1">
              <TrendingUp className="w-4 h-4" />
              Melhor Mês
            </div>
            <div className="text-lg font-bold text-green-900 dark:text-green-100 truncate">
              {statistics.bestMonth?.month_name || 'N/A'}
            </div>
            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
              {statistics.bestMonth?.people_count.toLocaleString('pt-BR')} pessoas
            </div>
          </div>
          <div className={`bg-gradient-to-br rounded-xl p-4 border ${
            statistics.trend === 'up'
              ? 'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700'
              : statistics.trend === 'down'
              ? 'from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200 dark:border-red-700'
              : 'from-gray-50 to-gray-100 dark:from-gray-700/20 dark:to-gray-600/20 border-gray-200 dark:border-gray-600'
          }`}>
            <div className={`text-sm font-medium mb-1 flex items-center gap-1 ${
              statistics.trend === 'up' ? 'text-green-600 dark:text-green-400'
              : statistics.trend === 'down' ? 'text-red-600 dark:text-red-400'
              : 'text-gray-600 dark:text-gray-400'
            }`}>
              {statistics.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : statistics.trend === 'down' ? <TrendingDown className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              Tendência
            </div>
            <div className={`text-2xl font-bold ${
              statistics.trend === 'up' ? 'text-green-900 dark:text-green-100'
              : statistics.trend === 'down' ? 'text-red-900 dark:text-red-100'
              : 'text-gray-900 dark:text-gray-100'
            }`}>
              {Math.abs(statistics.growth)}%
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* GRÁFICO */}
      {/* ============================================================ */}
      {viewMode === 'yearly' ? (
        yearlyComparisonData.length === 0 ? (
          <div className="h-96 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Dados insuficientes para comparativo anual</p>
              <p className="text-sm mt-1">Importe dados históricos para habilitar</p>
            </div>
          </div>
        ) : (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={yearlyComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                <XAxis dataKey="month_label" tick={{ fontSize: 12, fill: '#6b7280' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#6b7280' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip content={<YearlyTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value: string) => {
                    if (value.startsWith('year_')) return value.replace('year_', '');
                    if (value === 'diff_pct') return 'Variação (%)';
                    return value;
                  }}
                />
                <Bar yAxisId="left" dataKey={`year_${availableYears.slice(-2)[0]}`} name={`year_${availableYears.slice(-2)[0]}`} fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={30} />
                <Bar yAxisId="left" dataKey={`year_${availableYears.slice(-2)[1]}`} name={`year_${availableYears.slice(-2)[1]}`} fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={30} />
                <Line yAxisId="right" type="monotone" dataKey="diff_pct" name="diff_pct" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )
      ) : filteredData.length === 0 ? (
        <div className="h-96 flex items-center justify-center text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum dado disponível</p>
            <p className="text-sm mt-1">Aguardando dados dos últimos {monthsFilter} meses</p>
          </div>
        </div>
      ) : (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
              <XAxis dataKey="month_name" tick={{ fontSize: 11, fill: '#6b7280' }} angle={-45} textAnchor="end" height={100} />
              <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#6b7280' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#6b7280' }} />
              <Tooltip content={<SequentialTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value: string) => {
                  if (value === 'people_awflux') return 'AWFlux';
                  if (value === 'people_historical') return 'Dados Importados';
                  if (value === 'people_count') return 'Pessoas Detectadas';
                  if (value === 'growth_percentage') return 'Crescimento (%)';
                  return value;
                }}
              />
              {hasSeqHistorical ? (
                <>
                  <Bar yAxisId="left" dataKey="people_awflux" name="AWFlux" stackId="people" fill="#8b5cf6" radius={[0, 0, 0, 0]} />
                  <Bar yAxisId="left" dataKey="people_historical" name="Dados Importados" stackId="people" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                </>
              ) : (
                <Bar yAxisId="left" dataKey="people_count" name="Pessoas Detectadas" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
              )}
              <Line yAxisId="right" type="monotone" dataKey="growth_percentage" name="Crescimento (%)" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Legenda de fontes */}
      {viewMode === 'yearly' && yearlyStats && (
        <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-amber-500" />
            <span>{yearlyStats.yearA} (dados importados)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-purple-500" />
            <span>{yearlyStats.yearB} (AWFlux)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Variação %</span>
          </div>
        </div>
      )}
      {viewMode === 'sequential' && hasSeqHistorical && (
        <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-purple-500" />
            <span>AWFlux (detecção automática)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-amber-500" />
            <span>Dados importados (sistema anterior)</span>
          </div>
        </div>
      )}

      {/* Insight (modo sequencial) */}
      {viewMode === 'sequential' && filteredData.length > 0 && statistics.trend !== 'neutral' && (
        <div className={`mt-4 p-4 rounded-lg border ${
          statistics.trend === 'up'
            ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
        }`}>
          <p className={`text-sm font-medium ${
            statistics.trend === 'up' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
          }`}>
            💡 Insight:{' '}
            {statistics.trend === 'up'
              ? `Nos últimos ${monthsFilter} meses, o fluxo cresceu ${Math.abs(statistics.growth)}%. Continue monitorando essa tendência positiva!`
              : `Nos últimos ${monthsFilter} meses, o fluxo diminuiu ${Math.abs(statistics.growth)}%. Considere analisar possíveis causas dessa redução.`}
          </p>
        </div>
      )}

      {/* Insight (modo anual) */}
      {viewMode === 'yearly' && yearlyStats && yearlyStats.overallDiff !== 0 && (
        <div className={`mt-4 p-4 rounded-lg border ${
          yearlyStats.overallDiff > 0
            ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
        }`}>
          <p className={`text-sm font-medium ${
            yearlyStats.overallDiff > 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
          }`}>
            💡 Comparando {yearlyStats.yearA} com {yearlyStats.yearB}:{' '}
            {yearlyStats.overallDiff > 0
              ? `o fluxo cresceu ${yearlyStats.overallDiff}% (${(yearlyStats.totalB - yearlyStats.totalA).toLocaleString('pt-BR')} pessoas a mais).`
              : `o fluxo diminuiu ${Math.abs(yearlyStats.overallDiff)}% (${(yearlyStats.totalA - yearlyStats.totalB).toLocaleString('pt-BR')} pessoas a menos).`}
          </p>
        </div>
      )}
    </div>
  );
};
